const API = "/api";

/*
 * The website build plays one game per player (backend/players.py). Every
 * request to /api carries who is playing: a guest id this browser keeps
 * (X-GES-Guest) and, once signed in with Google, the Firebase ID token
 * (Authorization). Requests wait until sign-in state is known
 * (window.gesAuthReady), so a signed-in player never loads the guest game.
 *
 * This module loads first, so it also starts Firebase itself: it reads the
 * server's settings (GET /api/meta) and, only when Firebase is configured,
 * loads the SDK from Google's CDN and waits for the first sign-in state.
 * (Doing this in app/auth.js, which loads last, deadlocked: modules that
 * fetch while loading waited for sign-in, and sign-in waited for them.)
 */
const GES_GUEST_KEY = "ges.guestId";
const GES_FB_VERSION = "10.12.2";
const GES_NO_WAIT = /^\/api\/(meta|health)(\?|$)/;

function gesGuestId() {
  const make = () => (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID()
    : `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`);
  try {
    let id = localStorage.getItem(GES_GUEST_KEY);
    if (!id) { id = make(); localStorage.setItem(GES_GUEST_KEY, id); }
    return id;
  } catch (e) {
    window.__gesGuestFallback = window.__gesGuestFallback || make();
    return window.__gesGuestFallback;
  }
}

function gesScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src; s.async = true; s.onload = resolve; s.onerror = () => reject(new Error("Couldn't load Firebase."));
    document.head.appendChild(s);
  });
}

/** Firebase auth with the signed-in state known; or null (not configured, offline, or no browser). */
async function gesStartFirebase(raw) {
  let meta = null;
  try { meta = await (await raw("/api/meta")).json(); } catch (e) { return null; }
  window.gesMeta = meta;
  if (!meta || !meta.firebase || typeof document === "undefined") return null;
  try {
    await gesScript(`https://www.gstatic.com/firebasejs/${GES_FB_VERSION}/firebase-app-compat.js`);
    await gesScript(`https://www.gstatic.com/firebasejs/${GES_FB_VERSION}/firebase-auth-compat.js`);
    if (!window.firebase.apps.length) window.firebase.initializeApp(meta.firebase);
    const fb = window.firebase.auth();
    await new Promise((resolve) => {
      let first = true;
      fb.onIdTokenChanged(async (u) => {
        window.gesAuthToken = u ? await u.getIdToken() : null;
        if (first) { first = false; resolve(); }
      });
    });
    return fb;
  } catch (e) {
    return null;
  }
}

// a read repeated within GES_READ_REUSE_MS, with no change in between, is answered from the first one
const GES_READ_REUSE_MS = 5000; // long enough for a refresh's late readers to find the prefetched answers
const gesReadCache = new Map();
const gesCopy = (r) => (r && typeof r.clone === "function" ? r.clone() : r); // each reader gets its own body

if (typeof window !== "undefined") {
  window.gesGuestId = gesGuestId;
  if (typeof window.fetch === "function" && !window.__gesFetchWrapped) {
    const raw = window.fetch.bind(window);
    window.__gesFetchWrapped = true;
    // sign-in state is known before any game request goes out (at most 8 s: a slow CDN never blocks the game)
    window.gesFirebase = gesStartFirebase(raw);
    const giveUp = typeof setTimeout === "function" ? new Promise((r) => setTimeout(r, 8000)) : Promise.resolve();
    window.gesAuthReady = Promise.race([window.gesFirebase.then(() => undefined), giveUp]);
    window.fetch = async (input, init = {}) => {
      const url = typeof input === "string" ? input : (input && input.url) || "";
      const origin = (window.location && window.location.origin) || "";
      const path = origin && url.startsWith(origin) ? url.slice(origin.length) : url;
      if (!path.startsWith("/api/")) return raw(input, init);
      const method = ((init && init.method) || (typeof input !== "string" && input.method) || "GET").toUpperCase();
      if (path.startsWith("/api/forecast")) return gesRoute(raw, input, init, path, method); // has its own "running futures" note
      gesBusy(+1);
      try { return await gesRoute(raw, input, init, path, method); } finally { gesBusy(-1); }
    };
  }
}

/*
 * "Loading…" while the game waits for the server: a thin moving bar at the top and a small label, shown only
 * when a wait passes GES_BUSY_DELAY_MS (quick clicks never flicker). A wait past GES_BUSY_SLOW_MS explains
 * that the free server may be waking up.
 */
const GES_BUSY_DELAY_MS = 300;
const GES_BUSY_SLOW_MS = 8000;
let gesBusyCount = 0;
let gesBusyTimer = null;
let gesBusySlowTimer = null;

function gesBusy(delta) {
  if (typeof document === "undefined" || typeof setTimeout !== "function") return;
  gesBusyCount = Math.max(0, gesBusyCount + delta);
  if (gesBusyCount > 0 && !gesBusyTimer && !document.getElementById("ges-busy")) {
    gesBusyTimer = setTimeout(() => {
      gesBusyTimer = null;
      if (gesBusyCount === 0 || !document.body) return;
      const el = document.createElement("div");
      el.id = "ges-busy";
      el.className = "ges-busy";
      el.setAttribute("role", "status");
      el.innerHTML = `<div class="ges-busy-bar"></div><div class="ges-busy-label"><span class="ges-busy-dot"></span><span id="ges-busy-text">Loading…</span></div>`;
      document.body.appendChild(el);
      gesBusySlowTimer = setTimeout(() => {
        const t = document.getElementById("ges-busy-text");
        if (t) t.textContent = "Still loading… the server may be waking up (up to a minute)";
      }, GES_BUSY_SLOW_MS - GES_BUSY_DELAY_MS);
    }, GES_BUSY_DELAY_MS);
  }
  if (gesBusyCount === 0) {
    clearTimeout(gesBusyTimer); gesBusyTimer = null;
    clearTimeout(gesBusySlowTimer); gesBusySlowTimer = null;
    const el = document.getElementById("ges-busy");
    if (el) el.remove();
  }
}

/** The request itself: reads may be answered from a moment ago (gesReadCache); anything else clears it. */
async function gesRoute(raw, input, init, path, method) {
  if (method !== "GET") {
    gesReadCache.clear(); // anything that may change the game: the next reads ask the server again
    try { return await gesSend(raw, input, init, path); } finally { gesReadCache.clear(); }
  }
  // the same read twice within a moment, with nothing changed in between (the Overview and its chart
  // panel both ask for alerts and health, two modules ask for achievements): answer from the first
  if (!GES_NO_WAIT.test(path) && !(init && init.signal)) {
    gesLearnRead(path);
    const hit = gesReadCache.get(path);
    if (hit && Date.now() - hit.at < GES_READ_REUSE_MS) return hit.response.then(gesCopy);
    const response = gesSend(raw, input, init, path);
    gesReadCache.set(path, { at: Date.now(), response });
    response.then((r) => { if (!r.ok) gesReadCache.delete(path); }, () => gesReadCache.delete(path));
    return response.then(gesCopy);
  }
  return gesSend(raw, input, init, path);
}

/*
 * A refresh used to make its reads in a chain: the dashboard's own reads, then each module that adds a part
 * after the refresh (advisor, panels, unlocks, attention, reminders) waited for the one before it. With a
 * real internet round trip per step that was ~1.5 s of waiting. Each tab's refresh now learns which reads
 * it makes, and the next refresh of that tab starts them all at once, at the beginning; the modules then
 * find their answers already there (the read reuse above). Reads only, so starting early changes nothing.
 */
const GES_LEARN_MS = 4000;
const gesTabReads = new Map(); // tab -> the reads its last refresh made
let gesLearning = null;

function gesLearnRead(path) {
  if (gesLearning && Date.now() < gesLearning.until) gesLearning.paths.add(path);
}

function gesFinishLearning() {
  if (gesLearning && gesLearning.paths.size) gesTabReads.set(gesLearning.tab, [...gesLearning.paths]);
  gesLearning = null;
}

/** Called at the very start of a refresh (app/shell.js refreshAll). */
function gesPrefetchRefresh(tab) {
  if (typeof window === "undefined" || typeof setTimeout !== "function") return;
  gesFinishLearning();
  (gesTabReads.get(tab) || []).forEach((path) => { window.fetch(path).catch(() => {}); });
  gesLearning = { tab, paths: new Set(), until: Date.now() + GES_LEARN_MS };
  setTimeout(() => { if (gesLearning && gesLearning.tab === tab) gesFinishLearning(); }, GES_LEARN_MS);
}

if (typeof window !== "undefined") window.gesPrefetchRefresh = gesPrefetchRefresh;

/** One /api request with the player's identity (guest id, and the Google token once signed in). */
async function gesSend(raw, input, init, path) {
  if (!GES_NO_WAIT.test(path)) await window.gesAuthReady;
  const extra = { "X-GES-Guest": gesGuestId() };
  if (window.gesAuthToken) extra.Authorization = `Bearer ${window.gesAuthToken}`;
  let headers;
  if (typeof Headers === "function") {
    headers = new Headers(init.headers || (typeof input !== "string" && input.headers) || {});
    Object.entries(extra).forEach(([k, v]) => headers.set(k, v));
  } else headers = { ...(init.headers || {}), ...extra };
  const go = () => raw(input, { ...init, headers });
  const method = ((init && init.method) || "GET").toUpperCase();
  if (method !== "GET") return go();
  // reads go out at most GES_MAX_READS at a time, and a "too busy" answer (429/503: the server's per-player
  // limit, or a free server waking up) is waited out and retried instead of leaving a page blank
  await gesReadSlot();
  try {
    let res = await go();
    for (let i = 0; i < GES_RETRY_WAITS.length && (res.status === 429 || res.status === 503); i++) {
      const after = Number(res.headers && res.headers.get && res.headers.get("Retry-After"));
      await gesSleep(Number.isFinite(after) && after > 0 ? Math.min(after * 1000, 10000) : GES_RETRY_WAITS[i]);
      res = await go();
    }
    return res;
  } finally { gesReadDone(); }
}

const GES_MAX_READS = 6;
const GES_RETRY_WAITS = [800, 2000, 5000];
let gesReadsOut = 0;
const gesReadQueue = [];
const gesSleep = (ms) => new Promise((r) => (typeof setTimeout === "function" ? setTimeout(r, ms) : r()));

function gesReadSlot() {
  if (gesReadsOut < GES_MAX_READS) { gesReadsOut++; return Promise.resolve(); }
  return new Promise((r) => gesReadQueue.push(r));
}

function gesReadDone() {
  const next = gesReadQueue.shift();
  if (next) next(); else gesReadsOut = Math.max(0, gesReadsOut - 1);
}

/**
 * A server error page is HTML, not JSON ("Unexpected token '<'"): read the body as text and turn it into a
 * plain message. Used by every JSON reader (postJSON, ui/kit.js uiGet).
 */
async function gesReadJSON(res) {
  if (typeof res.text !== "function") { // a minimal response (tests): JSON only
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { const err = new Error(body.detail || `Request failed (${res.status})`); err.status = res.status; throw err; }
    return body;
  }
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : {}; } catch (e) { data = null; }
  if (!res.ok) {
    const busy = res.status === 429 || res.status === 503;
    const msg = (data && (typeof data.detail === "string" ? data.detail : data.message)) ||
      (busy ? "The server is busy right now. Wait a moment and try again." : `The server had a problem (error ${res.status}). Try again.`);
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  if (data === null) throw new Error("The server sent back something unexpected. Try again in a moment.");
  return data;
}

/** POST JSON; `opts.signal` (an AbortController's) cancels it, e.g. a forecast nobody will read. */
async function postJSON(path, body, opts = {}) {
  const headers = { "Content-Type": "application/json" };
  if (window.gesAuthToken) headers.Authorization = `Bearer ${window.gesAuthToken}`; // auth-ready API (backend/auth.py)
  const res = await fetch(`${API}${path}`, { method: "POST", headers, body: JSON.stringify(body), signal: opts.signal });
  return gesReadJSON(res);
}

export { API, gesReadJSON, postJSON };
